/**
 * Single source of truth for editable pages on the marketing site.
 *
 * Both `PageEditingSection` (inline edit launcher) and `PagesManagementSection`
 * (SEO/hero/structured content editor) consume this list. Adding a new page
 * here exposes it everywhere the admin needs it, so the two surfaces never
 * drift again.
 *
 * Capability flags
 *  - hasInlineEdit  — page renders <EditableText>/<EditableImage>; admin can
 *                     enable Edit Mode and tweak inline.
 *  - hasSeoMeta     — Page Manager SEO tab (title, description, OG image,
 *                     keywords, robots) is meaningful for this page.
 *  - hasHero        — Page Manager Hero tab (heroTitle, heroSubtitle, etc.)
 *                     applies. Skipped for legal/dynamic pages.
 *  - hasContent     — Page Manager Content tab (mission/body/team/openings/...)
 *                     applies. Used by long-form pages.
 *
 * Gating
 *  - feature        — feature flag key (from PLAN_FEATURES). If set and the
 *                     site has this feature disabled, the page entry is
 *                     filtered out for that admin. Surfaces only appear once
 *                     the feature is enabled in marketing-site settings.
 *  - dynamic        — page route only renders for live data (search results,
 *                     hotel detail). It still has SEO meta, but no hero or
 *                     inline edit because the body changes per request.
 *
 * Inline-edit fields (description shown in admin card)
 *  - inlineFields   — short human-readable list of what's editable inline.
 */

import {
    Home, MapPin, BookOpen, HelpCircle, Phone, Briefcase, Newspaper,
    ShieldCheck, Tag, Moon, Plane, Map as MapIcon, Globe, Building2,
    FileText, Settings, Search, CreditCard, Calendar, Lock, Sparkles,
} from 'lucide-react';

export const PAGE_REGISTRY = [
    // ─── Main marketing pages ──────────────────────────────────────────────
    {
        id: 'home', label: 'Home', path: '/', icon: Home,
        group: 'Main Pages',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: true,
        inlineFields: ['Hero headline & subtitle', 'Trending destinations heading', 'Featured properties heading', 'Newsletter heading'],
    },
    {
        id: 'destinations', label: 'Destinations', path: '/destinations', icon: MapPin,
        group: 'Main Pages', feature: 'destinations',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: false,
        inlineFields: ['Hero heading & subtitle', 'Section headings'],
    },
    {
        id: 'hotels', label: 'Hotels', path: '/hotels', icon: Building2,
        group: 'Main Pages', feature: 'hotelsAny',
        hasInlineEdit: false, hasSeoMeta: true, hasHero: true, hasContent: false,
        inlineFields: [],
    },
    {
        // server/lib/seo.js already resolves '/nearby' → hm_page_nearby, but the
        // page was missing from this registry, so its SEO fields were unreachable
        // in Page Manager and it could never be given FAQs.
        id: 'nearby', label: 'Hotels Near You', path: '/nearby', icon: MapPin,
        group: 'Main Pages', feature: 'nearbyHotels',
        hasInlineEdit: false, hasSeoMeta: true, hasHero: false, hasContent: false,
        inlineFields: [],
    },
    {
        id: 'flights', label: 'Flights', path: '/flights', icon: Plane,
        group: 'Main Pages', feature: 'flights',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: false,
        inlineFields: ['Hero heading & subtitle (via Hero Banner)'],
    },
    {
        id: 'tours', label: 'Tours', path: '/tours', icon: MapIcon,
        group: 'Main Pages', feature: 'tours',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: false,
        inlineFields: ['Hero heading & subtitle', 'Section headings'],
    },
    {
        id: 'experiences', label: 'Experiences', path: '/experiences', icon: Sparkles,
        group: 'Main Pages', feature: 'tours',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: false,
        inlineFields: ['Hero heading & subtitle', 'Highlights heading'],
    },
    {
        id: 'deals', label: 'Deals', path: '/deals', icon: Tag,
        group: 'Main Pages', feature: 'deals',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: false,
        inlineFields: ['Hero heading & subtitle', 'Section headings'],
    },
    {
        id: 'offline-hotels', label: 'Partner Hotels', path: '/hotels-partner', icon: Building2,
        group: 'Main Pages', feature: 'offlineHotels',
        hasInlineEdit: false, hasSeoMeta: true, hasHero: true, hasContent: false,
        inlineFields: [],
    },
    {
        // Partner-only list (no supplier hotels) — "View all partner hotels".
        id: 'partner-hotels', label: 'All Partner Hotels', path: '/partner-hotels', icon: Building2,
        group: 'Main Pages', feature: 'offlineHotels',
        hasInlineEdit: false, hasSeoMeta: true, hasHero: false, hasContent: false,
        inlineFields: [],
    },
    {
        id: 'hajj-umrah', label: 'Hajj & Umrah', path: '/hajj-umrah', icon: Moon,
        group: 'Main Pages', feature: 'hajjUmrah',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: true,
        inlineFields: ['Hero heading & subtitle', 'Why Choose Us section'],
    },
    {
        id: 'ummrah-planner', label: 'Umrah Planner', path: '/ummrah-planner', icon: Moon,
        group: 'Main Pages', feature: 'ummrahPlanner',
        hasInlineEdit: false, hasSeoMeta: true, hasHero: false, hasContent: false,
    },
    {
        id: 'visa', label: 'Visa Information', path: '/visa', icon: ShieldCheck,
        group: 'Main Pages', feature: 'visa',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: true,
        inlineFields: ['Hero heading & subtitle', 'CTA section'],
    },
    {
        id: 'blog', label: 'Blog', path: '/blog', icon: BookOpen,
        group: 'Main Pages', feature: 'blog',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: false,
        inlineFields: ['Hero heading & subtitle'],
    },

    // ─── Company pages ─────────────────────────────────────────────────────
    {
        id: 'about', label: 'About Us', path: '/about', icon: Globe,
        group: 'Company',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: true,
        // Hero headline/image, mission, body copy and the team roster are edited
        // via Pages Management → About Us (Hero/Content tabs), not inline — only
        // badges, stats and values/CTA microcopy are true EditableText fields.
        inlineFields: ['Badges & labels', 'Stats values', 'Values cards', 'CTA microcopy'],
    },
    {
        // Careers is a standard always-available company page — no feature gate
        // (same as About / Contact / FAQ). Admins still control its content via
        // Page Manager.
        id: 'careers', label: 'Careers', path: '/careers', icon: Briefcase,
        group: 'Company',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: true,
        inlineFields: ['Hero, benefits, job listings'],
    },
    {
        id: 'press', label: 'Press', path: '/press', icon: Newspaper,
        group: 'Company',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: true,
        inlineFields: ['Hero heading & subtitle', 'Press items'],
    },
    {
        id: 'contact', label: 'Contact', path: '/contact', icon: Phone,
        group: 'Company',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: true,
        inlineFields: ['Hero heading & subtitle'],
    },
    {
        id: 'support', label: 'Support', path: '/support', icon: HelpCircle,
        group: 'Company', feature: 'support',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: true,
        inlineFields: ['Hero heading & subtitle', 'FAQ items'],
    },
    {
        id: 'faq', label: 'FAQ', path: '/faq', icon: HelpCircle,
        group: 'Company',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: true, hasContent: true,
        inlineFields: ['Hero heading', 'all Q&A items'],
    },
    {
        // Auto-built from this registry + live content, so there is nothing to
        // edit beyond its own SEO meta — no hero, no content, no inline fields.
        id: 'sitemap', label: 'Sitemap', path: '/sitemap', icon: MapIcon,
        group: 'Company',
        hasInlineEdit: false, hasSeoMeta: true, hasHero: false, hasContent: false,
        inlineFields: [],
    },
    {
        // Long-form legal text — body is edited via Pages Management → Content tab
        // (a textarea is better than inline for legal copy). Heading and "Last updated"
        // are inline-editable on the page itself.
        id: 'terms', label: 'Terms & Conditions', path: '/terms', icon: FileText,
        group: 'Company',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: false, hasContent: true,
        inlineFields: ['Heading & last-updated date'],
    },
    {
        id: 'privacy', label: 'Privacy Policy', path: '/privacy', icon: Lock,
        group: 'Company',
        hasInlineEdit: true, hasSeoMeta: true, hasHero: false, hasContent: true,
        inlineFields: ['Heading & last-updated date'],
    },

    // ─── Dynamic / Booking pages (SEO meta only — no hero/content) ─────────
    {
        id: 'search-results', label: 'Search Results', path: '/search', icon: Search,
        group: 'Booking & Travel', dynamic: true, feature: 'hotelsAny',
        hasInlineEdit: false, hasSeoMeta: true, hasHero: false, hasContent: false,
    },
    {
        id: 'hotel-detail', label: 'Hotel Detail', path: '/hotel-detail-data', icon: Building2,
        group: 'Booking & Travel', dynamic: true, feature: 'hotelsAny',
        hasInlineEdit: false, hasSeoMeta: true, hasHero: false, hasContent: false,
    },
    {
        id: 'booking-form', label: 'Book Now / Form', path: '/confirm-booking', icon: CreditCard,
        group: 'Booking & Travel', dynamic: true, feature: 'hotelsAny',
        hasInlineEdit: false, hasSeoMeta: true, hasHero: false, hasContent: false,
    },
    {
        id: 'my-bookings', label: 'My Bookings', path: '/my-bookings', icon: Calendar,
        group: 'Booking & Travel', dynamic: true,
        hasInlineEdit: false, hasSeoMeta: true, hasHero: false, hasContent: false,
    },
];

// localStorage key prefix for per-page settings (matches existing pattern).
export const pageStorageKey = (id) => `hm_page_${id}`;

// Convenience filters used by both admin sections.
export const groupedRegistry = (entries = PAGE_REGISTRY) => {
    const groups = {};
    for (const p of entries) {
        const g = p.group || 'Other';
        if (!groups[g]) groups[g] = [];
        groups[g].push(p);
    }
    return groups;
};

// Returns only entries the current site is allowed to manage.
// Pages without a `feature` are always shown. Pages gated by a feature only
// appear once that feature is enabled in marketing-site settings.
export const visibleForFeatures = (features, capability = null) =>
    PAGE_REGISTRY.filter(p => {
        if (capability && !p[capability]) return false;
        if (!p.feature) return true;
        // Imported lazily to avoid circular deps in tests
        const { isFeatureEnabled, isAnyHotelsEnabled } = require('../utils/siteFeatures');
        if (p.feature === 'hotelsAny') return isAnyHotelsEnabled(features);
        return isFeatureEnabled(features, p.feature);
    });
